import { z } from "zod";
const id = z.string().min(1).max(128);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
export const nativeCodegenDependencySchema = z
  .object({
    projectId: id,
    pkgVersionId: id,
    projectRevId: id,
    expectedSourceSha256: digest,
  })
  .strict();
export const assistantCodegenFamilyRequestSchema = z
  .object({
    projectId: id,
    projectRevId: id,
    revision: z.number().int().positive(),
    branchId: id.nullable(),
    pkgVersionId: id.optional(),
    expectedSourceSha256: digest,
    expectedContext: z.string().min(1).max(256),
    scheme: z.enum(["plain", "blackbox"]),
    platform: z
      .enum(["react", "nextjs", "gatsby", "tanstack"])
      .default("react"),
    platformVersion: z.string().min(1).max(128).optional(),
    platformOptions: z
      .object({ nextjs: z.object({ appDir: z.boolean() }).strict().optional() })
      .strict()
      .default({}),
    stylesOpts: z
      .object({ scheme: z.enum(["css", "css-modules"]) })
      .strict()
      .default({ scheme: "css-modules" }),
    dependencies: z.array(nativeCodegenDependencySchema).max(64),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.platform !== "nextjs" && value.platformOptions.nextjs)
      ctx.addIssue({
        code: "custom",
        message: "Next options require Next platform",
      });
    const seen = new Set<string>();
    for (const node of value.dependencies) {
      if (seen.has(node.pkgVersionId))
        ctx.addIssue({
          code: "custom",
          message: "Duplicate dependency identity",
        });
      seen.add(node.pkgVersionId);
    }
  });
export type AssistantCodegenFamilyRequest = z.input<
  typeof assistantCodegenFamilyRequestSchema
>;
export interface AssistantCodegenFamilyResult {
  profile: "actor_bound_native_graph_v1";
  projectId: string;
  generationOptions: {
    platform: "react" | "nextjs" | "gatsby" | "tanstack";
    platformVersion?: string;
    platformOptions: { nextjs?: { appDir: boolean } };
    stylesOpts: { scheme: "css" | "css-modules" };
  };
  closureSha256: string;
  sources: {
    projectId: string;
    sourceId: string;
    projectRevId: string;
    revision: number;
    branchId: string | null;
    version: string;
    sourceSha256: string;
    dependencyIds: string[];
  }[];
  files: {
    path: string;
    bytes: string;
    encoding: "utf8" | "base64";
    size: number;
    sha256: string;
  }[];
  manifestSha256: string;
  delivery: "inline_native_graph";
}
